/**
 * Payment Slice
 *
 * Fetches payment detail via beaconApi.getPayment().
 * Uses createAsyncThunk with requestId tracking for race condition protection.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { paymentInitialState, PaymentDetailData } from './paymentState';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';

export const fetchPayment = createAsyncThunk<
  PaymentDetailData, string, { state: RootState; rejectValue: string }
>('payment/fetch', async (paymentId: string, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    const result = await beaconApi.getPayment(paymentId, apiBase);
    return result as PaymentDetailData;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch payment');
  }
});

const paymentSlice = createSlice({
  name: 'payment',
  initialState: paymentInitialState,
  reducers: {
    clearPayment: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.currentRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayment.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchPayment.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchPayment.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to fetch payment';
      });
  },
});

export const { clearPayment } = paymentSlice.actions;
export default paymentSlice.reducer;
